import { Scheduler, Observable, Subject, BehaviorSubject, NEVER, EMPTY, race, of } from 'rxjs'
import { distinctUntilChanged, ignoreElements, timeout, filter, catchError } from 'rxjs/operators'
import { webSocket, WebSocketSubject } from 'rxjs/webSocket'
import { mergeWithCompletion, AnonymousSubject } from 'utils'
import { RxWebSocketStatus, RxWebSocketReadyState, compareWebSocketStatus } from './RxWebSocketStatus'

export type WebSocketFactory = (url?: string, protocol?: string | Array<string>) => WebSocket

interface WebSocketCtor {
  new (url: string, protocols?: string | string[]): WebSocket
}

function makeWebSocketCtor(webSocketFactory: WebSocketFactory): WebSocketCtor {
  /* This needs to be a non-arrow function because it will be newed */
  return function(url: string, protocol?: string | Array<string>) {
    return webSocketFactory(url, protocol)
  } as any
}

export class RxWebSocket {
  private scheduler?: Scheduler
  private endpoint: string
  private socketSubject: WebSocketSubject<Uint8Array>
  private outgoingSubject: Subject<Uint8Array>
  private statusSubject: BehaviorSubject<RxWebSocketStatus>

  public get status$(): Observable<RxWebSocketStatus> {
    return this.socketSubject.pipe(
      ignoreElements(),
      catchError((evt: ErrorEvent) =>
        of<RxWebSocketStatus>({
          readyState: RxWebSocketReadyState.Errored,
          error: evt.error,
          endpoint: this.endpoint
        })
      ),
      mergeWithCompletion(this.statusSubject),
      distinctUntilChanged(compareWebSocketStatus)
    )
  }

  public get incoming$(): Observable<Uint8Array> {
    return this.socketSubject.pipe(catchError(() => EMPTY))
  }

  public get outgoing$(): Subject<Uint8Array> {
    return this.outgoingSubject
  }

  constructor(endpoint: string, scheduler?: Scheduler, webSocketFactory?: WebSocketFactory) {
    this.scheduler = scheduler
    this.endpoint = endpoint
    this.statusSubject = new BehaviorSubject<RxWebSocketStatus>({ readyState: RxWebSocketReadyState.Connecting, endpoint })
    this.socketSubject = webSocket({
      url: endpoint,
      binaryType: 'arraybuffer',
      serializer: data => data,
      deserializer: e => new Uint8Array(e.data),
      WebSocketCtor: webSocketFactory ? makeWebSocketCtor(webSocketFactory) : WebSocket,
      openObserver: {
        next: () => this.statusSubject.next({ readyState: RxWebSocketReadyState.Open, endpoint })
      },
      closingObserver: {
        next: () => this.statusSubject.next({ readyState: RxWebSocketReadyState.Closing, endpoint })
      },
      closeObserver: {
        next: (e: CloseEvent) => {
          this.statusSubject.next({
            readyState: RxWebSocketReadyState.Closed,
            endpoint,
            closeCode: e.code || null,
            closeReason: e.reason || null
          })
          this.statusSubject.complete()
        }
      }
    })
    this.outgoingSubject = new AnonymousSubject<Uint8Array>({
      next: data => {
        // WebSocketSubject buffers messages received when the socket is not connected, but we do not
        // want to do that as the messages will get lost if the endpoint never accepts our connection
        if (this.statusSubject.getValue().readyState !== RxWebSocketReadyState.Open) {
          throw new Error('Attempted to write to disconnected socket')
        } else {
          this.socketSubject.next(data)
        }
      },
      error: err => this.socketSubject.error(err),
      complete: () => this.socketSubject.complete()
    })
  }

  // returns an observable which errors if the socket fails to connect or times out
  // if the socket connects successfully it completes when the socket is closed
  public connect(timeoutMS: number): Observable<never> {
    return race(
      this.socketSubject.pipe(
        ignoreElements(),
        catchError((evt: ErrorEvent) => {
          throw evt.error
        })
      ),
      this.status$.pipe(filter(status => status.readyState === RxWebSocketReadyState.Open)),
      NEVER.pipe(timeout(timeoutMS, this.scheduler))
    ).pipe(ignoreElements())
  }
}
